// Story 8.13 (ADO-2022), Task 7.1 — the multi-line Form Field (Figma "Textarea": "Multi-line
// input. Same fill, stroke, radius and placeholder as the field inputs. Minimum 112px tall; grows
// with content."). First used by the Fulfilment card's Special instructions.
//
// It shares `FormField`'s label, helper, error and invalid styles and its a11y contract: an
// explicit <label htmlFor> when a `label` is given, else the caller names it with `aria-label` or
// `aria-labelledby` (the Fulfilment card's Special instructions takes `aria-label`, as its visible
// name is the toggle above it), helper and error text wired via aria-describedby,
// the error announced (`role="alert"`), and `aria-invalid` in the invalid state.
//
// Grows with content: the height follows the content from the 112px minimum up to
// {@link TEXT_AREA_MAX_HEIGHT_PX}, and only then scrolls, so a long note (up to 1,000 characters)
// never pushes the rest of the card off screen. There is no resize handle: the height is the
// content's.

import { useCallback, useId, useLayoutEffect, useRef } from 'react';
import type { ReactNode, TextareaHTMLAttributes } from 'react';
import styles from './FormField.module.css';

/** Figma "Textarea": the minimum height. */
export const TEXT_AREA_MIN_HEIGHT_PX = 112;
/** The height past which the field scrolls instead of growing (about 11 lines at 20px). */
export const TEXT_AREA_MAX_HEIGHT_PX = 240;

export interface FormTextAreaProps extends Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'id'> {
  /** The visible label. Omit it only when `aria-label` or `aria-labelledby` names the field
   *  instead. */
  label?: ReactNode;
  /** Optional helper text shown below the field. */
  helperText?: ReactNode;
  /** Error message; when set the field renders its invalid state. */
  error?: ReactNode;
  id?: string;
}

/** The vertical border widths a `border-box` height must include. */
function verticalBorder(element: HTMLElement): number {
  const style = getComputedStyle(element);
  return (parseFloat(style.borderTopWidth) || 0) + (parseFloat(style.borderBottomWidth) || 0);
}

export function FormTextArea({
  label,
  helperText,
  error,
  id,
  className,
  onInput,
  value,
  ...rest
}: FormTextAreaProps) {
  const reactId = useId();
  const fieldId = id ?? reactId;
  const helperId = helperText ? `${fieldId}-helper` : undefined;
  const errorId = error ? `${fieldId}-error` : undefined;
  const describedBy = [errorId, helperId].filter(Boolean).join(' ') || undefined;
  const fieldRef = useRef<HTMLTextAreaElement>(null);

  /** Size the field to its content, between the minimum and the maximum. */
  const fitToContent = useCallback(() => {
    const field = fieldRef.current;
    if (field === null) return;
    field.style.height = 'auto';
    const content = field.scrollHeight + verticalBorder(field);
    const height = Math.min(Math.max(content, TEXT_AREA_MIN_HEIGHT_PX), TEXT_AREA_MAX_HEIGHT_PX);
    field.style.height = `${height}px`;
    field.style.overflowY = content > TEXT_AREA_MAX_HEIGHT_PX ? 'auto' : 'hidden';
  }, []);

  // On mount, and whenever a controlled value changes (a value set from outside, too).
  useLayoutEffect(fitToContent, [fitToContent, value]);

  const textAreaClasses = [styles.input, styles.textArea, error ? styles.invalid : '', className]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={styles.field}>
      {label === undefined ? null : (
        <label className={styles.label} htmlFor={fieldId}>
          {label}
        </label>
      )}
      <textarea
        ref={fieldRef}
        id={fieldId}
        className={textAreaClasses}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy}
        {...(value === undefined ? {} : { value })}
        onInput={(event) => {
          fitToContent();
          onInput?.(event);
        }}
        {...rest}
      />
      {/* As in FormField: both can render, so every aria-describedby id resolves. */}
      {error ? (
        <span id={errorId} className={`${styles.helper} ${styles.error}`} role="alert">
          {error}
        </span>
      ) : null}
      {helperText ? (
        <span id={helperId} className={styles.helper}>
          {helperText}
        </span>
      ) : null}
    </div>
  );
}
